import { useCallback, useEffect, useMemo, useState } from "react";
import { Library, Search, AlertTriangle, Eye, Loader2, Check, X, RefreshCw, Mail, ClipboardCheck, TrendingUp, BookOpen, FileText, Wrench, ArrowUp, Send, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { ApiService } from "../../services/api";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import type { ObserveBooks, ObserveStudentRow, ObserveStudentDetail, ObserveLetter, ObserveExam, ObserveYolView, ObservePlanView, ObserveCallList } from "../../types/navigator";
import { ARAMA_DURUM, SONUC_AD, ARAMA_TUR, SMS_AD, MEMNUNIYET_AD } from "./aramaEtiketleri";

// Koç Gözlem — yönetici / öğretmen / rehberlik (navigator_observe). Departman kapsamı sunucuda:
// süper admin ve user_list hepsini, öğretmen yalnız sorumlu olduğu departmanları görür.
// Kontrolcü = mevcut öğretmenler + rehberlik (kocum-sistem-semasi.md 3.4).
// Görünüm E-Eğitim/Koçum ile aynı: TabPage kabuğu, mavi vurgu, altı çizili sekmeler (panel standardı).

const KIND_LABEL: Record<string, string> = { WELCOME: "Hoş geldin", NOTE: "Öğretmen notu", REMINDER: "Hatırlatma", CONTRACT: "Sözleşme", SUNDAY: "Haftanın sözü", MIDWEEK: "Ara not", RECKONING: "Hesap", CONCERN: "Endişe", CLOSING: "Kapanış" };
const STATUS_LABEL: Record<string, string> = { PENDING: "onay bekliyor", APPROVED: "onaylandı", SENT: "gönderildi", ANSWERED: "cevaplandı", SKIPPED: "atlandı" };
const SEV: Record<string, string> = { RISK: "bg-red-100 text-red-800", WATCH: "bg-amber-100 text-amber-800", INFO: "bg-gray-100 text-gray-700" };
const VERDICT: Record<string, string> = { biliyor: "bg-emerald-100 text-emerald-800", yarim: "bg-amber-100 text-amber-800", bilmiyor: "bg-red-100 text-red-800", olculmedi: "bg-gray-100 text-gray-600" };
const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDay = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) : "—");

type Tab = "ozet" | "plan" | "calisma" | "kitap" | "mudahale" | "defter" | "mektup" | "hazir" | "yorunge";
const SURELER = [60, 90, 120, 180, 240];
const SET_KIND: Record<string, string> = { READINESS: "Ölçüm", MAKEUP: "Telafi", SECTION_EXAM: "Bölüm sınavı", RETENTION: "Tekrar" };
const fmtNet = (n: number) => n.toLocaleString("tr-TR", { maximumFractionDigits: 2 });

export default function CoachObservePage() {
  // tabs sabit kalmalı: TabPage her yeni dizide sekmeyi yeniden seçer (durum ObserveStudents'ta).
  const tabs: TabItemProps[] = useMemo(
    () => [{ type: "custom", title: "Öğrenciler", path: "students", isSearch: false, showToggle: false, customComponent: <ObserveStudents /> }],
    []
  );
  return <TabPage title="Koç Gözlem" tabs={tabs} />;
}

function ObserveStudents() {
  const [q, setQ] = useState("");
  const [risk, setRisk] = useState<string>("");
  const [rows, setRows] = useState<ObserveStudentRow[]>([]);
  const [count, setCount] = useState(0);
  const [scope, setScope] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ObserveStudentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("ozet");
  const [pending, setPending] = useState<ObserveLetter[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, p] = await Promise.all([ApiService.observeStudents({ q, risk, limit: 100 }), ApiService.getPendingCoachLetters().catch(() => ({ count: 0, items: [] }))]);
      setRows(res.items);
      setCount(res.count);
      setScope(res.scope);
      setPending(p.items);
    } catch (e: any) {
      setError(e?.message || "Liste yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [q, risk]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openStudent = useCallback(async (id: string) => {
    setSelected(id);
    setDetailLoading(true);
    try {
      setDetail(await ApiService.observeStudent(id));
    } catch (e: any) {
      setError(e?.message || "Öğrenci dosyası yüklenemedi");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const refreshDetail = useCallback(async () => {
    if (selected) await openStudent(selected);
    await load();
  }, [selected, openStudent, load]);

  return (
    <div className="px-3 sm:px-4">
      <div className="pb-10">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm text-gray-600">
              {count} öğrenci · kapsam: {scope === "hepsi" ? "tüm departmanlar" : scope === "departman" ? "sorumlu olduğun departmanlar" : "—"}
              {pending.length > 0 && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{pending.length} mektup onay bekliyor</span>}
            </p>
          </div>
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" /> Yenile
          </button>
        </div>

        <div className="grid gap-3 lg:grid-cols-[360px_1fr]">
          {/* Liste */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-3">
            <div className="mb-2 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, soyad, e-posta" className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-2 text-sm" />
              </div>
            </div>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {[["", "Hepsi"], ["RISK", "Risk"], ["WATCH", "İzle"], ["PENDING", "Onay bekleyen"], ["PAID", "Ödeyenler"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setRisk(v)} className={`rounded-full px-2.5 py-1 text-xs font-medium ${risk === v ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                  {l}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
            ) : rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">{q.trim().length >= 2 ? "Bu aramayla öğrenci bulunamadı." : risk === "PAID" ? "Kapsamında ödemesi şu an açık öğrenci yok." : "Kapsamında koç profili olan öğrenci yok. Programı olmayan öğrenciyi adıyla arayabilirsin."}</p>
            ) : (
              <div className="max-h-[70vh] space-y-1.5 overflow-y-auto pr-1">
                {rows.map((r) => (
                  <button key={r.id} type="button" onClick={() => openStudent(r.id)} className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${selected === r.id ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-gray-900">{r.firstName} {r.lastName}</span>
                      <span className="flex shrink-0 items-center gap-1">
                        {r.payment && (
                          <span title={`${r.payment.method === "free" ? "Ücretsiz paket" : r.payment.method === "cash" ? "Nakit ödeme" : "Kartla ödeme"} · ${fmtDay(r.payment.endsAt)} tarihine kadar açık`} className={`rounded-full px-1.5 text-[11px] font-semibold ${r.payment.method === "free" ? "bg-gray-100 text-gray-600" : "bg-emerald-100 text-emerald-800"}`}>
                            {r.payment.method === "free" ? "ücretsiz" : "₺"} {fmtDay(r.payment.endsAt)}
                          </span>
                        )}
                        {r.ara && <span title={r.ara.not || "Öğretmenin verdiği ara"} className="rounded-full bg-sky-100 px-1.5 text-[11px] font-semibold text-sky-800">ara</span>}
                        {r.risk > 0 && <span className="rounded-full bg-red-100 px-1.5 text-[11px] font-semibold text-red-800">{r.risk} risk</span>}
                        {r.watch > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">{r.watch}</span>}
                        {r.pendingLetters > 0 && <Mail className="h-3.5 w-3.5 text-amber-600" />}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-gray-500">
                      {r.department?.name || "—"} · {r.hasProfile === false ? <span className="font-medium text-amber-700">programı yok</span> : <>{r.targetExamLabel || r.targetExam || "—"} {r.puanTuru ? `· ${r.puanTuru}` : ""} · {r.weeklyMinutes} dk/hafta</>}
                    </div>
                    {r.lastObservation && <div className="mt-0.5 truncate text-[11px] text-gray-600">{r.lastObservation.summary}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dosya */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            {!selected ? (
              <div className="grid place-items-center py-16 text-center">
                <Eye className="mb-2 h-8 w-8 text-gray-300" />
                <p className="text-sm text-gray-500">Soldan bir öğrenci seç: defteri, mektupları, sözleri, hazırbulunuşluğu ve yörüngesi burada açılır.</p>
              </div>
            ) : detailLoading || !detail ? (
              <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
            ) : (
              <StudentFile detail={detail} tab={tab} setTab={setTab} onChanged={refreshDetail} />
            )}
            {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function StudentFile({ detail, tab, setTab, onChanged }: { detail: ObserveStudentDetail; tab: Tab; setTab: (t: Tab) => void; onChanged: () => Promise<void> }) {
  const s = detail.student;
  const tabs: Array<[Tab, string, any]> = [["ozet", "Özet", TrendingUp], ["plan", "Plan", CalendarDays], ["calisma", "Deneme ve testler", FileText], ["kitap", "Kitaplar", Library], ["mudahale", "Müdahale", Wrench], ["defter", "Defter", BookOpen], ["mektup", "Mektuplar", Mail], ["hazir", "Hazırbulunuşluk", ClipboardCheck], ["yorunge", "Yörünge", TrendingUp]];
  const pendingCount = detail.letters.filter((l) => l.status === "PENDING").length;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{s.firstName} {s.lastName}</h2>
          <p className="text-xs text-gray-500">{s.department?.name || "—"} · {s.email || ""} · kayıt {fmtDay(s.createdAt)}{!s.licenseExpiryDate && !s.lastPaymentDate ? " · lisans alanları boş" : ""}</p>
        </div>
        <div className="text-right text-xs text-gray-600">
          <div>{detail.model.text}</div>
          {detail.profile?.pace?.text && <div>{detail.profile.pace.text}</div>}
        </div>
      </div>
      <div className="mb-3 flex items-center gap-4 sm:gap-6 overflow-x-auto scrollbar-hide whitespace-nowrap border-b border-gray-200 text-sm text-gray-600">
        {tabs.map(([id, label, Icon]) => (
          <button key={id} type="button" onClick={() => setTab(id)} className={`inline-flex flex-shrink-0 items-center gap-1.5 pb-3 transition-all duration-200 ${tab === id ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"}`}>
            <Icon className="h-4 w-4" /> {label}
            {id === "mektup" && pendingCount > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">{pendingCount}</span>}
          </button>
        ))}
      </div>
      {tab === "ozet" && <Ozet detail={detail} />}
      {tab === "plan" && <PlanTab key={detail.student.id} userId={detail.student.id} />}
      {tab === "calisma" && <Calisma detail={detail} />}
      {tab === "kitap" && <Kitaplar key={detail.student.id} userId={detail.student.id} />}
      {tab === "mudahale" && <Mudahale key={detail.student.id} detail={detail} onChanged={onChanged} />}
      {tab === "defter" && <Defter detail={detail} />}
      {tab === "mektup" && <Mektuplar detail={detail} onChanged={onChanged} />}
      {tab === "hazir" && <Hazir detail={detail} />}
      {tab === "yorunge" && <Yorunge detail={detail} />}
    </div>
  );
}

export function Kitaplar({ userId, loader }: { userId: string; loader?: (id: string) => Promise<ObserveBooks> }) {
  const [data, setData] = useState<ObserveBooks | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    (loader || ((id: string) => ApiService.getObserveStudentBooks(id)))(userId)
      .then((res) => { if (alive) setData(res); })
      .catch((err) => { if (alive) setError(err?.message || "Kitaplar yüklenemedi."); });
    return () => { alive = false; };
  }, [userId, loader]);

  if (error) return <p className="text-sm text-gray-600">{error}</p>;
  if (!data) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />;
  if (!data.books.length) return <p className="text-sm text-gray-500">Öğrenci henüz kitap eklemedi.</p>;
  const hafta = data.daily.reduce((a, gun) => a + gun.questions, 0);
  const gunler = data.daily.filter((gun) => gun.tests > 0).length;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Son 7 gün: {hafta.toLocaleString("tr-TR")} soru, {gunler} gün çalışma (öğrenci beyanı)</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {data.books.map((book) => (
          <Card key={book.id} title={book.title}>
            <p className="text-xs text-gray-500">{[book.publisher, book.subject].filter(Boolean).join(" · ") || "—"}</p>
            <Row k="İlerleme" v={`${book.solvedTests} / ${book.testCount} test`} />
            <Row k="Soru" v={`${book.solvedQuestions.toLocaleString("tr-TR")} çözüldü · ${book.correct} D · ${book.wrong} Y · ${book.blank} B`} />
            <Row k="Başarı (öğrenci beyanı)" v={book.successPercent === null ? "—" : `%${book.successPercent}`} />
          </Card>
        ))}
      </div>
      {data.wrongs.length > 0 && (
        <Card title="Yanlış sorulardan çıkan kazanımlar">
          <ul className="space-y-1 text-xs text-gray-700">
            {data.wrongs.map((yanlis) => (
              <li key={yanlis.id}>{fmtDay(yanlis.createdAt)} · {yanlis.bookTitle} · {yanlis.testTitle}: {yanlis.gainText}</li>
            ))}
          </ul>
        </Card>
      )}
      {data.recent.length > 0 && (
        <Card title="Son kayıtlar">
          <ul className="space-y-1 text-xs text-gray-700">
            {data.recent.map((kayit) => (
              <li key={kayit.id}>{fmtDay(kayit.solvedOn)} · {kayit.bookTitle} · {kayit.testTitle}: {kayit.correct} D, {kayit.wrong} Y, {kayit.blank} B{kayit.minutes ? `, ${kayit.minutes} dk` : ""}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Ozet({ detail }: { detail: ObserveStudentDetail }) {
  const p = detail.profile;
  const t = detail.trajectory;
  const last7 = detail.observations.filter((o) => Date.now() - new Date(o.occurredAt).getTime() < 7 * 86400000);
  const openPromise = detail.promises.find((x) => x.status === "OPEN");
  const lastPromise = detail.promises.find((x) => x.status !== "OPEN" && x.status !== "REPLACED");
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Card title="Hedef ve söz">
        <Row k="Sınav" v={p ? `${p.targetExamLabel || p.targetExam || "—"}${p.puanTuru ? ` · ${p.puanTuru}` : ""}` : "koç profili yok"} />
        <Row k="Hedef sıralama" v={p?.hedefSiralama ? p.hedefSiralama.toLocaleString("tr-TR") : "—"} />
        <Row k="Müsaitlik" v={p?.weeklyAvailability ? `${Object.values(p.weeklyAvailability).filter((v) => Number(v) > 0).length} gün · ${Object.values(p.weeklyAvailability).reduce((a, v) => a + (Number(v) || 0), 0)} dk/hafta` : "girilmemiş"} />
        <Row k="Açık söz" v={openPromise ? `${openPromise.days} gün × ${openPromise.minutes} dk (${fmtDay(openPromise.weekStart)} haftası, ${openPromise.source === "STUDENT" ? "öğrenci" : "teklif"})` : "—"} />
        <Row k="Son söz sonucu" v={lastPromise ? `${lastPromise.status} — ${lastPromise.outcome?.actualDays ?? "?"} gün / ${lastPromise.outcome?.actualMinutes ?? "?"} dk` : "—"} />
        {p?.revisionPending && <p className="mt-1 text-xs text-amber-700">Plan revizyonu bekliyor (söz değişti, öğrenci "Planı güncelle" demeli).</p>}
      </Card>
      <Card title="Yörünge">
        {t?.uygulanabilir ? (
          <>
            <Row k="Mevcut" v={`${t.mevcut.puan} puan ≈ ${t.mevcut.yer} (${t.mevcut.kaynak})`} />
            {t.referans && <Row k="Referans çizgi" v={`${t.referans.yer} · ${t.referans.hafta ? `${t.referans.hafta}. haftada` : `kapanmıyor, eksik ${t.referans.eksikPuan} puan`}`} />}
            {t.hedef && <Row k="Öğrencinin hedefi" v={`${t.hedef.yer} · ${t.hedef.hafta ? `${t.hedef.hafta}. haftada` : `kapanmıyor, eksik ${t.hedef.eksikPuan} puan`}`} />}
            <Row k="Yol" v={`${t.konuSayisi} konu · ${t.toplamSaat} saat · söz ${t.soz.dakika} dk (etkin ${t.soz.etkinDakika})`} />
          </>
        ) : (
          <p className="text-sm text-gray-600">Hesaplanamıyor: {t?.neden || "—"}</p>
        )}
      </Card>
      <Card title="Son 7 gün (defter)">
        {last7.length === 0 ? <p className="text-sm text-gray-500">Kayıt yok.</p> : last7.slice(0, 6).map((o) => <ObsLine key={o.id} o={o} />)}
      </Card>
      <Card title="Kişisel model">
        <Row k="Doğruluk" v={`%${Math.round(detail.model.dogruluk * 100)}`} />
        <Row k="Söz tutma" v={`%${Math.round(detail.model.tutma * 100)}`} />
        <Row k="Hız" v={`${detail.model.hizCarpani.toFixed(1)}×`} />
        <Row k="Cevap" v={`%${Math.round(detail.model.cevapOrani * 100)} (n=${detail.model.n})`} />
        {detail.model.history?.length ? <p className="mt-1 text-[11px] text-gray-500">Son güncelleme: {fmt(detail.model.history[detail.model.history.length - 1]?.at)}</p> : null}
      </Card>
    </div>
  );
}

// Plan: öğretmen öğrencinin planını gün gün görür — ne verildi, ne bitti, ne kaçtı, plan nerede bitiyor.
const ITEM_STATUS: Record<string, { label: string; cls: string }> = {
  DONE: { label: "bitti", cls: "bg-emerald-100 text-emerald-800" },
  MISSED: { label: "kaçtı", cls: "bg-red-100 text-red-800" },
  PENDING: { label: "bekliyor", cls: "bg-gray-100 text-gray-700" },
  SKIPPED: { label: "atlandı", cls: "bg-gray-100 text-gray-500" },
};
const ITEM_TYPE: Record<string, string> = { BOOK: "Kitap testi", TEST: "Test", TEACH: "Konu", MAKEUP: "Tekrar", REVIEW: "Tekrar" };
const addDaysIso = (d: string, n: number) => { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

// Öğrencinin kendi Raporlarım'ı da kullanır (loader = ApiService.getMyCoachPlan).
export function PlanTab({ userId, loader, emptyText }: { userId: string; loader?: (range?: { from: string; to: string }) => Promise<ObservePlanView>; emptyText?: string }) {
  const [data, setData] = useState<ObservePlanView | null>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (loader ? loader(range || undefined) : ApiService.observePlan(userId, range || undefined))
      .then((d) => { if (!cancelled) { setData(d); setErr(null); } })
      .catch((e: any) => { if (!cancelled) setErr(e?.message || "Plan okunamadı"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, range, loader]);

  const shift = (weeks: number) => {
    const a = data?.aralik;
    if (!a) return;
    setRange({ from: addDaysIso(a.from, weeks * 7), to: addDaysIso(a.to, weeks * 7) });
  };

  if (loading && !data) return <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!data?.hasPlan || !data.plan || !data.sayi) return <p className="text-sm text-gray-500">{emptyText || "Öğrencinin aktif planı yok. Müdahale sekmesinden program kurabilirsin."}</p>;
  const p = data.plan;
  const s = data.sayi;
  const weeks: Array<NonNullable<ObservePlanView["gunler"]>> = [];
  (data.gunler || []).forEach((g, i) => { if (i % 7 === 0) weeks.push([]); weeks[weeks.length - 1].push(g); });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Plan">
          <Row k="Tür" v={p.haftalik ? `Yol · haftalık · günde ${p.gunlukDakika} dk` : p.mode} />
          <Row k="Başlangıç" v={fmtDay(p.baslangic)} />
          <Row k="Planın son günü" v={`${fmtDay(p.sonGun)}${p.haftalik ? " (her Pazar uzar)" : ""}`} />
          {p.yolBitti && <p className="mt-1 text-xs text-emerald-700">Yolun bütün işleri planlandı.</p>}
        </Card>
        <Card title="Durum">
          <Row k="Bitti" v={`${s.bitti} / ${s.toplam}`} />
          <Row k="Kaçırılan" v={String(s.kacti)} />
          <Row k="Bekleyen" v={String(s.bekliyor)} />
          {s.devredildi > 0 && <Row k="İleri kaydırılan" v={String(s.devredildi)} />}
          {data.siradaki && <p className="mt-1 text-xs text-gray-600">Sıradaki: <b>{data.siradaki.title}</b> · {fmtDay(data.siradaki.tarih)}{data.siradaki.blok ? ` · ${data.siradaki.blok}` : ""}</p>}
        </Card>
      </div>
      {p.not && <p className="text-xs text-gray-500">{p.not}</p>}

      <div className="flex items-center justify-between">
        <button type="button" onClick={() => shift(-1)} className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"><ChevronLeft className="h-3.5 w-3.5" /> Önceki hafta</button>
        <span className="text-xs text-gray-500">{data.aralik ? `${fmtDay(data.aralik.from)} – ${fmtDay(data.aralik.to)}` : ""}{loading ? " · yükleniyor…" : ""}</span>
        <button type="button" onClick={() => shift(1)} className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50">Sonraki hafta <ChevronRight className="h-3.5 w-3.5" /></button>
      </div>

      {weeks.map((w) => (
        <div key={w[0].tarih} className="rounded-lg border border-gray-200">
          <p className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600">
            {fmtDay(w[0].tarih)} haftası · {w.reduce((a, g) => a + g.kalemler.filter((i) => !i.devredildi).length, 0)} iş · {w.reduce((a, g) => a + g.kalemler.filter((i) => i.status === "DONE").length, 0)} bitti
          </p>
          <div className="divide-y divide-gray-100">
            {w.map((g) => (
              <div key={g.tarih} className={`px-3 py-2 ${g.bugun ? "bg-blue-50/60" : ""}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className={`font-medium ${g.bugun ? "text-blue-800" : "text-gray-800"}`}>{g.gun} · {fmtDay(g.tarih)}{g.bugun ? " · bugün" : ""}</span>
                  <span className="text-gray-500">{g.kalemler.length ? `${g.dakika} dk` : "boş"}</span>
                </div>
                {g.kalemler.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {g.kalemler.map((it) => {
                      const st = ITEM_STATUS[it.status] || ITEM_STATUS.PENDING;
                      return (
                        <li key={it.id} className={`flex items-center gap-2 text-xs ${it.devredildi ? "opacity-50" : ""}`}>
                          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`}>{it.devredildi ? "ileri kaydı" : st.label}</span>
                          <span className="shrink-0 text-gray-400">{ITEM_TYPE[it.type] || it.type}</span>
                          <span className="min-w-0 flex-1 truncate text-gray-800" title={it.title}>{it.title}</span>
                          {it.percent != null && <span className="shrink-0 font-medium text-emerald-700">%{it.percent}</span>}
                          <span className="shrink-0 text-gray-400">{it.dakika} dk</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Müdahale: öğretmen programı arkadan kurar, günlük süreyi/sırayı değiştirir, öğrenciye not yazar.
// Kaydedince bugünden sonrası yeni ayarla yeniden dizilir; biten işler korunur.
function Mudahale({ detail, onChanged }: { detail: ObserveStudentDetail; onChanged: () => Promise<void> }) {
  const id = detail.student.id;
  const [view, setView] = useState<ObserveYolView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dk, setDk] = useState<number>(120);
  const [pt, setPt] = useState<string>("SAY");
  const [atla, setAtla] = useState<Set<string>>(new Set());
  const [oneAl, setOneAl] = useState<string[]>([]);
  const [acik, setAcik] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [araBitis, setAraBitis] = useState("");
  const [araNot, setAraNot] = useState("");

  const apply = useCallback((v: ObserveYolView) => {
    setView(v);
    setDk(v.dailyMinutes || 120);
    setPt(v.puanTuru);
    setAtla(new Set(v.atla));
    setOneAl(v.oneAl);
  }, []);

  useEffect(() => {
    let cancelled = false;
    ApiService.observeYol(id).then((v) => { if (!cancelled) apply(v); }).catch((e: any) => { if (!cancelled) setErr(e?.message || "Yol okunamadı"); });
    return () => { cancelled = true; };
  }, [id, apply]);

  const konuOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of view?.bloklar || []) for (const s of b.bolumler) m.set(s.sectionId, s.konu);
    return m;
  }, [view]);

  const save = async () => {
    setBusy("save"); setErr(null); setMsg(null);
    try {
      // LGS'de puan türü yok: ekranın tuttuğu "LGS" gönderilmez (doğrulama yalnız SAY/EA/SÖZ kabul eder)
      const res = await ApiService.observeYolApply(id, { dailyMinutes: dk, puanTuru: view?.sinav === "LGS" ? undefined : pt, atla: [...atla], oneAl });
      apply(res.view);
      setMsg(res.kuruldu ? "Program kuruldu. Koç öğrenciye mektubunu birazdan yazacak." : "Program güncellendi; bugünden sonrası yeniden dizildi.");
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Kaydedilemedi");
    } finally {
      setBusy(null);
    }
  };

  // Ara (dış kaynak vb.): bitişe kadar koç plan kurmaz, mektup/hatırlatma göndermez, sessizlik kopma sayılmaz
  const araKaydet = async (bitir: boolean) => {
    setBusy("ara"); setErr(null); setMsg(null);
    try {
      if (bitir) await ApiService.observeEndAra(id);
      else await ApiService.observeSetAra(id, { bitis: araBitis || null, not: araNot.trim() || null });
      apply(await ApiService.observeYol(id));
      setMsg(bitir ? "Ara bitti; plan kaldığı yerden kuruldu." : "Ara verildi; bu sürede koç plan kurmaz ve hatırlatma göndermez.");
      if (!bitir) { setAraBitis(""); setAraNot(""); }
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Ara kaydedilemedi");
    } finally {
      setBusy(null);
    }
  };

  const sendNote = async () => {
    setBusy("note"); setErr(null); setMsg(null);
    try {
      await ApiService.observeSendNote(id, note.trim());
      setNote("");
      setMsg("Not gönderildi (uygulama içi, bildirim ve e-posta ayarlıysa e-posta).");
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Not gönderilemedi");
    } finally {
      setBusy(null);
    }
  };

  if (!view) return err ? <p className="text-sm text-red-600">{err}</p> : <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const toggleAtla = (sid: string) => { const n = new Set(atla); if (n.has(sid)) n.delete(sid); else { n.add(sid); setOneAl(oneAl.filter((x) => x !== sid)); } setAtla(n); };
  const toggleOne = (sid: string) => { if (oneAl.includes(sid)) setOneAl(oneAl.filter((x) => x !== sid)); else { setOneAl([...oneAl, sid]); const n = new Set(atla); n.delete(sid); setAtla(n); } };

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border p-3 text-sm ${view.yol ? "border-gray-200" : "border-amber-300 bg-amber-50/60"}`}>
        {view.yol
          ? <p className="text-gray-700">Öğrenci <b>yol</b> programında{view.kuran ? ` · son değişiklik: ${view.kuran.ad}, ${fmtDay(view.kuran.at)}` : ""}. Değişiklikte bugünden sonrası yeniden dizilir, biten işler korunur.</p>
          : view.uygun === false
            ? <p className="text-amber-900">Bu öğrenciye yol programı kurulamıyor: <b>{view.uygunDegil}</b>.</p>
            : <p className="text-amber-900">{detail.profile ? "Öğrenci eski (ölçümlü) programda." : "Öğrencinin koç programı yok."} Aşağıdan süreyi seçip <b>Programı kur</b> dersen yol programı kurulur; öğrencinin tanışma yapmasına gerek kalmaz.</p>}
      </div>

      <section>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Günlük süre (haftada 6 gün)</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {SURELER.map((m) => (
            <button key={m} type="button" onClick={() => setDk(m)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${dk === m ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{m % 60 === 0 ? `${m / 60} saat` : `${m} dk`}</button>
          ))}
          {!view.yol && view.sinav !== "LGS" && (
            <span className="ml-3 inline-flex items-center gap-1.5 text-xs text-gray-600">Puan türü:
              {["SAY", "EA", "SÖZ"].map((p) => (
                <button key={p} type="button" onClick={() => setPt(p)} className={`rounded-full px-2.5 py-1 font-medium ${pt === p ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{p}</button>
              ))}
            </span>
          )}
        </div>
      </section>

      <section>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Sıra · konuyu öne al ya da atla</p>
        {oneAl.length > 0 && (
          <p className="mb-2 text-xs text-blue-800">Öne alınanlar (bu sırayla başa gelir): {oneAl.map((sid, i) => `${i + 1}. ${konuOf.get(sid) || sid}`).join(" · ")}</p>
        )}
        <div className="space-y-1.5">
          {view.bloklar.map((b, i) => {
            const biten = b.bolumler.filter((s) => s.test > 0 && s.biten >= s.test).length;
            return (
              <div key={b.key} className="rounded-lg border border-gray-200">
                <button type="button" onClick={() => setAcik(acik === b.key ? null : b.key)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm">
                  <span className="text-gray-900"><span className="mr-1.5 text-gray-400">{i + 1}.</span>{b.ad}{b.eksik && <span className="ml-2 text-xs text-amber-700">dersi yok</span>}</span>
                  <span className="shrink-0 text-xs text-gray-500">{biten}/{b.bolumler.length} bölüm bitti{b.bolumler.some((s) => atla.has(s.sectionId)) ? ` · ${b.bolumler.filter((s) => atla.has(s.sectionId)).length} atlanıyor` : ""}</span>
                </button>
                {acik === b.key && (
                  <ul className="divide-y divide-gray-100 border-t border-gray-100">
                    {b.bolumler.map((s) => (
                      <li key={s.sectionId} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className={`min-w-0 flex-1 truncate ${atla.has(s.sectionId) ? "text-gray-400 line-through" : "text-gray-800"}`}>{s.konu}</span>
                        <span className="shrink-0 text-gray-500">{s.biten}/{s.test} test</span>
                        <button type="button" onClick={() => toggleOne(s.sectionId)} title="Öne al" className={`inline-flex items-center gap-1 rounded px-2 py-1 font-medium ${oneAl.includes(s.sectionId) ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}><ArrowUp className="h-3 w-3" /> öne al</button>
                        <button type="button" onClick={() => toggleAtla(s.sectionId)} title="Biliyor, atla" className={`rounded px-2 py-1 font-medium ${atla.has(s.sectionId) ? "bg-rose-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>atla</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={!!busy || (!view.yol && view.uygun === false)} className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {view.yol ? "Kaydet ve planı yeniden diz" : "Programı kur"}
        </button>
        {msg && <span className="text-xs text-emerald-700">{msg}</span>}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>

      <TelefonAramasi id={id} araAktif={!!view.araAktif} />

      <section className="border-t border-gray-200 pt-3">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Ara (dış kaynak, kitap vb.)</p>
        {view.araAktif && view.ara ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
            <span>
              Ara veriliyor: {fmtDay(view.ara.baslangic)} – {view.ara.bitis ? fmtDay(view.ara.bitis) : "öğretmen bitirene kadar"}
              {view.ara.not ? ` · ${view.ara.not}` : ""}{view.ara.kuran?.ad ? ` · ${view.ara.kuran.ad}` : ""}
            </span>
            <button type="button" onClick={() => araKaydet(true)} disabled={!!busy} className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-white px-3 py-1 text-xs font-medium text-sky-900 hover:bg-sky-100 disabled:opacity-50">
              {busy === "ara" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Arayı bitir
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600">Bitiş
              <input type="date" value={araBitis} onChange={(e) => setAraBitis(e.target.value)} className="mt-0.5 block rounded border border-gray-300 px-2 py-1 text-sm" />
            </label>
            <input value={araNot} onChange={(e) => setAraNot(e.target.value.slice(0, 300))} placeholder="Neden? (ör. matematik temel kitabı)" className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm" />
            <button type="button" onClick={() => araKaydet(false)} disabled={!!busy || !view.yol && !detail.profile} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50">
              {busy === "ara" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Ara ver
            </button>
            <p className="basis-full text-[11px] text-gray-500">Bu sürede koç plan kurmaz, mektup ve hatırlatma göndermez, sessizlik kopma sayılmaz. Bitiş boşsa siz bitirene kadar sürer.</p>
          </div>
        )}
      </section>

      <section className="border-t border-gray-200 pt-3">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Öğrenciye not yaz</p>
        <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 2000))} rows={3} placeholder="Kendi cümlenle yaz; öğrenci Mektuplar'da ve bildirim olarak görür, e-posta ayarlıysa e-postayla da gider." className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
        <button type="button" onClick={sendNote} disabled={!!busy || note.trim().length < 5} className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50">
          {busy === "note" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Notu gönder
        </button>
      </section>
    </div>
  );
}

// Koçun telefon araması (24 Eki): YES Tools'un 0282 hattından yapay zekâ asistanı öğrenciyi arar — kısa koç görüşmesi.
// Otomatik: 3 çalışma günüdür iş yoksa akşam saatlerinde; burada öğretmen şimdi başlatır ve geçmişi görür.
function TelefonAramasi({ id, araAktif }: { id: string; araAktif: boolean }) {
  const [data, setData] = useState<ObserveCallList | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [acik, setAcik] = useState<string | null>(null);
  const load = useCallback(() => {
    ApiService.observeTelefon(id).then(setData).catch(() => setData(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const ara = async () => {
    if (!window.confirm("Koçun asistanı bu öğrenciyi şimdi arasın mı?")) return;
    setBusy(true); setMsg(null);
    try {
      await ApiService.observeTelefonAra(id);
      setMsg("Arama başladı; sonuç birkaç dakika içinde burada görünür.");
      window.setTimeout(load, 15000);
    } catch (e: any) {
      setMsg(e?.message || "Arama başlatılamadı");
    } finally {
      setBusy(false);
      load();
    }
  };
  const yenidenAc = async () => {
    if (!window.confirm("Öğrenci asistanın aramasını istememişti. Onunla konuştunuz ve yeniden açılsın mı?")) return;
    setBusy(true); setMsg(null);
    try {
      await ApiService.observeTelefonAc(id);
      setMsg("Asistanın araması yeniden açıldı.");
    } catch (e: any) {
      setMsg(e?.message || "Açılamadı");
    } finally {
      setBusy(false);
      load();
    }
  };
  if (!data) return null;
  const engelli = !!data.kapali || data.yanlisNumara;
  return (
    <section className="border-t border-gray-200 pt-3">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Telefon · koçun asistanı</p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={ara} disabled={busy || !data.hazir || !data.telefon || araAktif || engelli} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Asistan şimdi arasın
        </button>
        <span className="text-[11px] text-gray-500">
          {!data.hazir ? "Arama altyapısı bu kurumda ayarlı değil." : !data.telefon ? "Öğrencinin kayıtlı cep telefonu yok." : data.kapali ? `Öğrenci asistanın aramasını istemedi${data.kapali.at ? ` (${fmtDay(data.kapali.at)})` : ""}; aranmaz.` : data.yanlisNumara ? "Kayıtlı numara yanlış numara çıktı; telefonu güncelleyince yeniden aranır." : araAktif ? "Arada; aranmaz." : "3 çalışma günüdür iş yoksa akşam saatlerinde kendiliğinden de arar. Öğretmen araması 09:00–21:00."}
        </span>
        {data.kapali && (
          <button type="button" onClick={yenidenAc} disabled={busy} className="text-xs font-medium text-blue-700 hover:underline disabled:opacity-50">Yeniden aç</button>
        )}
        {msg && <span className="basis-full text-xs text-emerald-700">{msg}</span>}
      </div>
      {data.aramalar.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
          {data.aramalar.map((c) => (
            <li key={c.id} className="px-3 py-2 text-xs">
              <button type="button" onClick={() => setAcik(acik === c.id ? null : c.id)} className="flex w-full flex-wrap items-center gap-x-2 text-left">
                <span className="text-gray-500">{fmtDay(c.createdAt)}</span>
                <span className="font-medium text-gray-800">{c.outcome ? SONUC_AD[c.outcome.durum] || c.outcome.durum : ARAMA_DURUM[c.status] || c.status}</span>
                <span className="text-gray-400">{ARAMA_TUR[c.reason] || "otomatik"}{c.durationSec ? ` · ${Math.round(c.durationSec / 60)} dk` : ""}</span>
                {c.outcome?.ogretmeneHaber && <span className="rounded-full bg-red-100 px-1.5 text-[11px] font-semibold text-red-800">öğretmene haber</span>}
                {c.outcome?.aranmakIstemiyor && <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">aranmak istemiyor</span>}
                {c.outcome?.memnuniyet && <span className={`rounded-full px-1.5 text-[11px] font-semibold ${c.outcome.memnuniyet === "MEMNUN" ? "bg-emerald-100 text-emerald-800" : c.outcome.memnuniyet === "MEMNUN_DEGIL" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"}`}>{MEMNUNIYET_AD[c.outcome.memnuniyet]}</span>}
                {(c.sms || []).map((m) => (
                  <span key={m.tur} className={`rounded-full px-1.5 text-[11px] font-semibold ${m.ok === false ? "bg-gray-100 text-gray-600" : "bg-blue-100 text-blue-800"}`}>{SMS_AD[m.tur] || m.tur} SMS'i {m.ok === false ? "gitmedi" : "gönderildi"}</span>
                ))}
              </button>
              {c.outcome?.ozet && <p className="mt-0.5 text-gray-700">{c.outcome.ozet}</p>}
              {c.outcome?.istek && <p className="mt-0.5 text-amber-800">İstek: {c.outcome.istek}</p>}
              {(c.outcome?.engel || c.outcome?.bugunkuIs) && (
                <p className="mt-0.5 text-gray-500">{c.outcome.engel ? `Engel: ${c.outcome.engel}` : ""}{c.outcome.engel && c.outcome.bugunkuIs ? " · " : ""}{c.outcome.bugunkuIs ? `Söz: ${c.outcome.bugunkuIs}${c.outcome.neZaman ? ` (${c.outcome.neZaman})` : ""}` : ""}</p>
              )}
              {acik === c.id && c.transcript && c.transcript.length > 0 && (
                <div className="mt-1.5 space-y-0.5 rounded bg-gray-50 p-2">
                  {c.transcript.map((t, i) => (
                    <p key={i}><b className="text-gray-600">{t.role === "assistant" ? "Asistan" : "Öğrenci"}:</b> {t.text}</p>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Calisma({ detail }: { detail: ObserveStudentDetail }) {
  const w = detail.work;
  if (!w) return <p className="text-sm text-gray-500">Çalışma verisi yok.</p>;
  const openSets = w.sets.filter((s) => s.status !== "COMPLETED");
  return (
    <div className="space-y-4">
      {w.hata && <p className="text-xs text-red-600">Çalışma verisi okunamadı: {w.hata}</p>}

      <section>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Denemeler</p>
        {w.exams.length === 0 ? <p className="text-sm text-gray-500">Deneme sonucu yok.</p> : <div className="space-y-2">{w.exams.map((e) => <ExamCard key={e.id} e={e} />)}</div>}
      </section>

      <section>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Föy testleri · ders özeti</p>
        {w.tests.summary.length === 0 ? (
          <p className="text-sm text-gray-500">Föy testi yok.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr><th className="px-2 py-1">Ders</th><th className="px-2 py-1">Biten</th><th className="px-2 py-1">Yarım</th><th className="px-2 py-1">D / Y / B</th><th className="px-2 py-1">Başarı</th><th className="px-2 py-1">Son</th></tr>
              </thead>
              <tbody>
                {w.tests.summary.map((g) => (
                  <tr key={g.lessonId} className="border-t border-gray-100">
                    <td className="px-2 py-1 font-medium text-gray-900">{g.lesson}</td>
                    <td className="px-2 py-1">{g.completed}</td>
                    <td className={`px-2 py-1 ${g.inProgress ? "font-semibold text-amber-700" : ""}`}>{g.inProgress}</td>
                    <td className="px-2 py-1">{g.questions ? `${g.correct} / ${g.wrong} / ${g.blank}` : "—"}</td>
                    <td className="px-2 py-1">{g.percent != null ? `%${g.percent}` : "—"}</td>
                    <td className="px-2 py-1 text-gray-600">{fmtDay(g.lastAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {w.tests.recent.length > 0 && (
        <section>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Son föy testleri {w.tests.total > w.tests.recent.length ? `(son ${w.tests.recent.length} / ${w.tests.total})` : ""}</p>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-left text-gray-600">
                <tr><th className="px-2 py-1">Tarih</th><th className="px-2 py-1">Ders · bölüm · test</th><th className="px-2 py-1">D / Y / B</th><th className="px-2 py-1">%</th><th className="px-2 py-1">Süre</th></tr>
              </thead>
              <tbody>
                {w.tests.recent.map((t) => (
                  <tr key={t.id} className={`border-t border-gray-100 ${t.status !== "COMPLETED" ? "bg-amber-50/60" : ""}`}>
                    <td className="whitespace-nowrap px-2 py-1 text-gray-600">{fmt(t.endedAt || t.startedAt)}</td>
                    <td className="px-2 py-1 text-gray-800">{t.lesson} · {t.section} · {t.test}{t.status !== "COMPLETED" && <span className="ml-1 text-amber-700">(yarım bırakıldı)</span>}</td>
                    <td className="px-2 py-1">{t.total != null ? `${t.correct ?? 0} / ${t.wrong ?? 0} / ${t.blank ?? 0}` : "—"}</td>
                    <td className="px-2 py-1">{t.percent != null ? t.percent : "—"}</td>
                    <td className="px-2 py-1 text-gray-600">{t.minutes != null ? `${t.minutes} dk` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {w.sets.length > 0 && (
        <section>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Koç setleri {openSets.length > 0 && <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold normal-case text-amber-800">{openSets.length} yarım</span>}</p>
          <div className="space-y-1">
            {w.sets.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-gray-800"><span className="text-gray-500">{SET_KIND[s.kind] || s.kind} · </span>{s.title}</span>
                <span className={`text-xs ${s.status === "COMPLETED" ? "text-gray-600" : "font-semibold text-amber-700"}`}>
                  {s.status === "COMPLETED" ? `bitti · ${s.correct ?? 0}/${s.total ?? 0} doğru · ${fmtDay(s.completedAt)}` : `yarım · ${s.solved ?? 0}/${s.total ?? 0} cevap · ${fmtDay(s.startedAt)}`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ExamCard({ e }: { e: ObserveExam }) {
  const sessions = Object.entries(e.sessions);
  return (
    <div className={`rounded-lg border p-3 ${e.partial ? "border-amber-300 bg-amber-50/50" : "border-gray-200"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-semibold text-gray-900">{e.examName}</span>
          <span className="ml-2 text-xs text-gray-500" title="Sonucun sisteme yazıldığı gün; çözüm günü ayrıca tutulmuyor">{fmtDay(e.resultAt)} (sonuç girişi)</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {sessions.map(([k, s]) => (
            <span key={k} className={`rounded px-1.5 py-0.5 ${s.answered === 0 ? "bg-gray-100 text-gray-500" : "bg-blue-50 text-blue-800"}`}>
              {k}: {s.answered === 0 ? "girmedi" : `${fmtNet(s.net)} net · %${Math.round(s.completion * 100)} dolu`}
            </span>
          ))}
          {e.score != null && !e.partial && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{e.scoreLabel || "Puan"} {fmtNet(e.score)}</span>}
        </div>
      </div>
      {e.partial && <p className="mt-1 text-xs text-amber-800">Yarım deneme: ana oturumun yarısından azı cevaplanmış. Puan ve sıralama yorumlanmamalı.</p>}
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-gray-500"><tr><th className="py-0.5 pr-2">Test</th><th className="py-0.5 pr-2">D</th><th className="py-0.5 pr-2">Y</th><th className="py-0.5 pr-2">B</th><th className="py-0.5 pr-2">Net</th></tr></thead>
          <tbody>
            {e.tests.filter((t) => t.correct + t.incorrect > 0 || t.session !== "AYT" || (e.sessions.AYT?.answered ?? 0) > 0).map((t) => (
              <tr key={t.no} className="border-t border-gray-100">
                <td className="py-0.5 pr-2 text-gray-800">{t.session !== "Toplam" && t.session !== "LGS" ? `${t.session} ` : ""}{t.name}</td>
                <td className="py-0.5 pr-2">{t.correct}</td>
                <td className="py-0.5 pr-2">{t.incorrect}</td>
                <td className={`py-0.5 pr-2 ${t.total && t.blank / t.total >= 0.5 ? "font-semibold text-amber-700" : ""}`}>{t.blank}</td>
                <td className={`py-0.5 pr-2 font-medium ${t.net < 0 ? "text-red-700" : "text-gray-900"}`}>{fmtNet(t.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Defter({ detail }: { detail: ObserveStudentDetail }) {
  return (
    <div className="space-y-1.5">
      {detail.observations.length === 0 ? <p className="text-sm text-gray-500">Defter boş.</p> : detail.observations.map((o) => <ObsLine key={o.id} o={o} full />)}
    </div>
  );
}

function ObsLine({ o, full = false }: { o: ObserveStudentDetail["observations"][number]; full?: boolean }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className={`mt-0.5 shrink-0 rounded px-1.5 text-[10px] font-semibold ${SEV[o.severity] || SEV.INFO}`}>{o.severity}</span>
      <span className="min-w-0 flex-1 text-gray-800">
        <span className="text-gray-500">{fmtDay(o.occurredAt)} · {o.kind}</span> — {o.summary}
        {full && o.usedInLetterId && <span className="ml-1 text-[10px] text-blue-600">mektupta kullanıldı</span>}
      </span>
    </div>
  );
}

function Mektuplar({ detail, onChanged }: { detail: ObserveStudentDetail; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const act = async (fn: () => Promise<any>, key: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      await onChanged();
    } catch (e: any) {
      setMsg(e?.message || "İşlem başarısız");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500">Elle üret:</span>
        {(["CONTRACT", "SUNDAY", "MIDWEEK", "RECKONING"] as const).map((k) => (
          <button key={k} type="button" disabled={!!busy} onClick={() => act(() => ApiService.observeGenerateLetter(detail.student.id, k), `gen-${k}`)} className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:opacity-60">
            {busy === `gen-${k}` ? <Loader2 className="inline h-3 w-3 animate-spin" /> : KIND_LABEL[k]}
          </button>
        ))}
        {msg && <span className="text-xs text-red-600">{msg}</span>}
      </div>
      {detail.letters.length === 0 && <p className="text-sm text-gray-500">Henüz mektup yok. Pazar 20:00'de ilk sözleşme mektubu üretilir.</p>}
      {detail.letters.map((l) => {
        const pendingL = l.status === "PENDING";
        const problems = (l.validation || []).flatMap((v) => v.problems || []);
        return (
          <div key={l.id} className={`rounded-lg border p-3 ${pendingL ? "border-amber-300 bg-amber-50/50" : "border-gray-200 bg-white"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm">
                <span className="font-semibold text-gray-900">{l.subject}</span>
                <span className="ml-2 text-xs text-gray-500">{KIND_LABEL[l.kind] || l.kind} · {STATUS_LABEL[l.status] || l.status} · {fmt(l.sentAt || l.createdAt)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className={`rounded px-1.5 py-0.5 ${l.generator === "llm" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}`}>{l.generator === "llm" ? "üretildi" : "şablon"}</span>
                {problems.length > 0 && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800" title={problems.join(" | ")}>{problems.length} doğrulama notu</span>}
                {l.bodyEdited && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">insan düzeltti</span>}
              </div>
            </div>
            {pendingL ? (
              <>
                <textarea value={edits[l.id] ?? l.body} onChange={(e) => setEdits({ ...edits, [l.id]: e.target.value })} rows={9} className="mt-2 w-full rounded border border-gray-300 px-2 py-1.5 text-sm leading-relaxed" />
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" disabled={!!busy} onClick={() => act(() => ApiService.approveCoachLetter(l.id, edits[l.id] && edits[l.id] !== l.body ? edits[l.id] : null), `ok-${l.id}`)} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                    {busy === `ok-${l.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Onayla ve gönder
                  </button>
                  <button type="button" disabled={!!busy} onClick={() => act(() => ApiService.skipCoachLetter(l.id, "gözlemci atladı"), `skip-${l.id}`)} className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-60">
                    <X className="h-4 w-4" /> Atla
                  </button>
                  <span className="self-center text-[11px] text-gray-500">Düzeltirsen, düzeltmen sonraki mektuplara örnek olur.</span>
                </div>
              </>
            ) : (
              <div className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-800">{l.bodyEdited || l.body}</div>
            )}
            {(l.predictions || l.outcome || l.answer) && (
              <div className="mt-2 grid gap-1 text-[11px] text-gray-600 sm:grid-cols-3">
                {l.predictions?.ask && <span>Tahmin: {l.predictions.ask.days} gün × {l.predictions.ask.minutes} dk{l.predictions.beklenenPuan != null ? ` · beklenen ${l.predictions.beklenenPuan} puan` : ""}</span>}
                {l.outcome && <span>Gerçek: {l.outcome.actualDays ?? "?"} gün / {l.outcome.actualMinutes ?? "?"} dk{l.outcome.status ? ` · ${l.outcome.status}` : ""}</span>}
                {l.answer && <span>Cevap: {l.answer.accept === true ? "kabul" : l.answer.accept === false ? `değiştirdi (${l.answer.days} × ${l.answer.minutes})` : "yazdı"}{l.answer.text ? ` — "${l.answer.text}"` : ""}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Hazir({ detail }: { detail: ObserveStudentDetail }) {
  const r = detail.readiness;
  if (!r || !r.uygulanabilir) return <p className="text-sm text-gray-500">Hazırbulunuşluk uygulanamıyor: {r?.hata || "kapsam yok (profil / eşleme)"}.</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(r.ozet || {}).map(([k, v]) => (
          <span key={k} className={`rounded-full px-2 py-0.5 font-medium ${VERDICT[k] || VERDICT.olculmedi}`}>{k}: {String(v)}</span>
        ))}
        <span className="text-gray-500">{r.tamamlandi ? "pencere tamamlandı" : "pencere açık"}</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {r.lessons.map((l: any) => (
          <div key={l.lessonId} className="rounded-lg border border-gray-200 p-2 text-sm">
            <div className="flex items-center justify-between"><span className="font-medium text-gray-900">{l.lessonTitle}</span><span className="text-xs text-gray-500">{l.durum}{l.percent != null ? ` · %${l.percent}` : ""}</span></div>
            <div className="mt-1 flex flex-wrap gap-1">
              {r.konular.filter((k: any) => k.lessonId === l.lessonId).map((k: any) => (
                <span key={k.konuId} className={`rounded px-1.5 py-0.5 text-[11px] ${VERDICT[k.verdict] || VERDICT.olculmedi}`} title={k.total != null ? `${k.correct}/${k.total}` : "ölçülmedi"}>{k.ad}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Yorunge({ detail }: { detail: ObserveStudentDetail }) {
  const t = detail.trajectory;
  if (!t?.uygulanabilir) return <p className="text-sm text-gray-500">Yörünge hesaplanamıyor: {t?.neden || "—"}</p>;
  const weeks = (t.haftalar || []).slice(0, 26);
  const max = Math.max(...weeks.map((w: any) => w.beklenenPuan), t.referans?.puan || 0, t.hedef?.puan || 0);
  const min = Math.min(...weeks.map((w: any) => w.beklenenPuan), t.mevcut.puan);
  const pct = (v: number) => (max === min ? 50 : ((v - min) / (max - min)) * 100);
  return (
    <div className="space-y-3">
      <div className="grid gap-1 text-sm sm:grid-cols-3">
        <Row k="Mevcut" v={`${t.mevcut.puan} · ${t.mevcut.yer}`} />
        {t.referans && <Row k="Referans" v={`${t.referans.puan} · ${t.referans.yer}${t.referans.hafta ? ` · ${t.referans.hafta}. hafta` : " · kapanmıyor"}`} />}
        <Row k="Söz" v={`${t.soz.dakika} dk/hafta · etkin ${t.soz.etkinDakika} · tutma %${Math.round(t.model.tutma * 100)}`} />
      </div>
      <div className="relative h-40 rounded-lg border border-gray-200 bg-gray-50 p-2">
        {t.referans && <div className="absolute left-0 right-0 border-t border-dashed border-red-400" style={{ bottom: `${8 + pct(t.referans.puan) * 0.84}%` }} title={`referans ${t.referans.puan}`} />}
        {t.hedef && <div className="absolute left-0 right-0 border-t border-dashed border-blue-400" style={{ bottom: `${8 + pct(t.hedef.puan) * 0.84}%` }} title={`hedef ${t.hedef.puan}`} />}
        <div className="flex h-full items-end gap-[2px]">
          {weeks.map((w: any) => (
            <div key={w.hafta} className="flex-1 rounded-t bg-blue-500/80" style={{ height: `${8 + pct(w.beklenenPuan) * 0.84}%` }} title={`${w.hafta}. hafta (${w.tarih}): ${w.beklenenPuan} puan · ${w.kapanan.length} konu`} />
          ))}
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-left text-gray-600"><tr><th className="px-2 py-1">Hafta</th><th className="px-2 py-1">Tarih</th><th className="px-2 py-1">Beklenen</th><th className="px-2 py-1">Kapanan konular</th></tr></thead>
          <tbody>
            {weeks.map((w: any) => (
              <tr key={w.hafta} className="border-t border-gray-100"><td className="px-2 py-1">{w.hafta}</td><td className="px-2 py-1">{w.tarih || "—"}</td><td className="px-2 py-1">{w.beklenenPuan}{w.yer ? ` · ${w.yer}` : ""}</td><td className="px-2 py-1 text-gray-700">{w.kapanan.map((k: any) => k.konu).join(", ") || "—"}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      {t.ozelDers?.length > 0 && <p className="text-xs text-gray-600 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Özel ders adayı: {t.ozelDers.map((k: any) => k.konu).join(", ")}</p>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: any }) {
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      {children}
    </div>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3 py-0.5 text-sm"><span className="text-gray-500">{k}</span><span className="text-right text-gray-900">{v}</span></div>
  );
}
